import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { EmojiResource } from '@atlaskit/emoji/resource';

import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { CustomGreetingConfig } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';

import { createNavigateToObject } from '../utils/navigation.ts';
import { FeedbackVariant } from '../utils/FeedbackVariant.ts';
import { useLazyShowFeedbackFlag } from '../utils/useLazyShowFeedbackFlag.ts';

const CLOSE_MODAL_DELAY = 500;

export const useConversationAssistantConfig = ({
	assistanceServiceConfigHeaders = {},
	preventAbortRequestsOnStagingModalClose = false,
	preventAbortRequestsOnStandaloneChatClose = false,
}: {
	assistanceServiceConfigHeaders?: Record<string, string>;
	preventAbortRequestsOnStagingModalClose?: boolean;
	preventAbortRequestsOnStandaloneChatClose?: boolean;
}) => {
	const { cloudId, orgId, userId } = useSessionData();
	const emojiProvider = useMemo(() => {
		return getEmojiProvider(EmojiResource, {
			cloudId,
			userId,
		});
	}, [userId, cloudId]);

	return {
		displaySubmitFlag: true,
		siteId: cloudId,
		product: 'confluence',
		orgId,
		assistanceServiceConfig: {
			headers: assistanceServiceConfigHeaders,
			baseUrl: '/gateway/api/assist',
		},
		emojiProvider,
		userId: userId ?? undefined,
		preventAbortRequestsOnStagingModalClose,
		preventAbortRequestsOnStandaloneChatClose,
	};
};

export const useConversationAssistantUiConfig = ({
	flags,
	customGreeting,
	shouldShowGreeting,
	shouldHideFeedback,
	entryPoint,
}: {
	flags: FlagsStateContainer;
	customGreeting?: CustomGreetingConfig;
	shouldShowGreeting?: boolean;
	shouldHideFeedback?: boolean;
	entryPoint?: string;
}) => {
	const { push } = useRouteActions();
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();

	const navigateToObject = useCallback(
		async ({ baseUrl: baseUrlNav, path }: { baseUrl: string; path: string }) => {
			createNavigateToObject(push)({ baseUrl: baseUrlNav, path });
			// artifical delay before closing the modal gives the app a moment to transition to the created object while the modal is still open, this creates a smoother transition from modal to new object.
			// For non-SPA transitions, a full page reload will happen so this delay will be ignored
			await new Promise((r) => setTimeout(r, CLOSE_MODAL_DELAY));
		},
		[push],
	);

	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	const handleCloseSmartCreate = useCallback(
		({
			currentConversationId,
			metadataActionKey,
			isRemixGenerated,
		}: {
			currentConversationId?: string | null;
			metadataActionKey?: string;
			isRemixGenerated?: boolean;
		}) => {
			if (shouldHideFeedback) {
				return;
			}

			// for the most part, we want to show the feedback flag corresponding to the type of content that was last generated in the modal regardless of the entry point.
			// there is one edge case: if the ephemeral preview modal was just opened from a remix entry point, isRemixGenerated will be undefined so we need to check the entry point.
			const isRemixForFeedback =
				typeof isRemixGenerated === 'boolean' ? isRemixGenerated : entryPoint === 'dynamicUi';

			void showCreateWithRovoModalCloseFeedbackFlag({
				feedbackVariant: isRemixForFeedback
					? FeedbackVariant.AFTER_REMIX_MODAL_CLOSE
					: FeedbackVariant.AFTER_AIFC_MODAL_CLOSE,
				contentType: undefined,
				cloudId,
				currentConversationId: currentConversationId ?? undefined,
				metadataActionKey,
			});
		},
		[showCreateWithRovoModalCloseFeedbackFlag, shouldHideFeedback, cloudId, entryPoint],
	);

	return {
		customHandlers: {
			handleSubmitSmartCreate: navigateToObject,
			handleCloseSmartCreate,
		},
		elements: {
			shouldShowGreeting: shouldShowGreeting ?? undefined,
		},
		customGreeting: customGreeting ?? undefined,
	};
};
